import { useState } from 'react';
import { Badge, Button, Checkbox, Input, Modal, useToast } from '../../components/primitives';
import { printToPdf } from '../../lib/printToPdf';
import { ApiError } from '../../lib/api';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocContactQR } from '../../components/document';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  useDeleteTelefonniCislo,
  useSaveTelefonniCislo,
  useTelefonniCisla,
  type TelefonniCislo,
  type TelefonniCisloData,
} from './telefonniCisla';
import styles from '../ucet/Ucet.module.css';

interface Kontakt {
  id: string;
  typ: 'vlastni' | 'izs' | 'util' | 'energy';
  role: string;
  name: string;
  phone: string;
  /** Pro QR vizitku (firma / organizace). */
  org?: string;
  note?: string;
  /** Vlastní číslo uživatele (lze upravit/smazat). */
  custom?: TelefonniCislo;
}

/** IZS — statické nouzové linky, tisknuté vždy (dle originální BOZP šablony). */
const izsContacts: Kontakt[] = [
  { id: 'izs-112', typ: 'izs', role: 'Tísňové volání', name: 'Tísňová linka 112', org: 'IZS', phone: '112' },
  { id: 'izs-155', typ: 'izs', role: 'Zdravotnická záchranka', name: 'Záchranná služba', org: 'RZS', phone: '155' },
  { id: 'izs-150', typ: 'izs', role: 'Hasičský záchranný sbor', name: 'Hasiči', org: 'HZS', phone: '150' },
  { id: 'izs-158', typ: 'izs', role: 'Policie ČR', name: 'Policie ČR', org: 'PČR', phone: '158' },
  { id: 'izs-156', typ: 'izs', role: 'Městská policie', name: 'Městská policie', org: 'MP', phone: '156' },
];

/** Pohotovosti sítí — plyn / elektřina / voda / kanalizace. */
const utilityContacts: Kontakt[] = [
  { id: 'util-plyn', typ: 'util', role: 'Pohotovost plyn', name: 'Plynová pohotovost', org: 'Distribuce', phone: '1239' },
  { id: 'util-el', typ: 'util', role: 'Pohotovost elektřina', name: 'Elektrická pohotovost', org: 'Distribuce', phone: '224 915 151' },
  { id: 'util-voda', typ: 'util', role: 'Pohotovost voda', name: 'Vodárenská pohotovost', org: 'Distribuce', phone: '267 310 543' },
  { id: 'util-kanal', typ: 'util', role: 'Pohotovost kanalizace', name: 'Kanalizační pohotovost', org: 'Distribuce', phone: '284 013 221' },
];

/** Energetika Praha — dodavatelé sítí v Praze. */
const energyContacts: Kontakt[] = [
  { id: 'e-pre', typ: 'energy', role: 'Zákaznická linka', name: 'PRE — Pražská energetika', org: 'PRE', phone: '840 550 055' },
  { id: 'e-ppas', typ: 'energy', role: 'Zákaznická linka', name: 'Pražská plynárenská', org: 'PPaS', phone: '800 134 134' },
  { id: 'e-pvk', typ: 'energy', role: 'Zákaznická linka', name: 'PVK — Praha vodovody', org: 'PVK', phone: '840 111 112' },
  { id: 'e-veolia', typ: 'energy', role: 'Zákaznická linka', name: 'Veolia Energie Praha', org: 'Veolia', phone: '221 511 925' },
  { id: 'e-thmp', typ: 'energy', role: 'Zákaznická linka', name: 'THMP', org: 'THMP', phone: '800 404 060' },
  { id: 'e-tsk', typ: 'energy', role: 'Dispečink', name: 'TSK Praha', org: 'TSK', phone: '257 015 111' },
];

const typMap: Record<Kontakt['typ'], { tone: 'danger' | 'yellow' | 'neutral' | 'success'; label: string }> = {
  vlastni: { tone: 'neutral', label: 'Vlastní' },
  izs: { tone: 'danger', label: 'IZS' },
  util: { tone: 'yellow', label: 'Pohotovost' },
  energy: { tone: 'success', label: 'Energetika Praha' },
};

/** Sestaví samostatné, stylované A4 HTML telefonního seznamu pro PDF. */
function buildPhoneListHtml(rows: Kontakt[], stavbaNazev?: string): string {
  const esc = (s: string) =>
    (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const body = rows
    .map(
      (k) => `<tr>
        <td class="cat">${esc(typMap[k.typ].label)}</td>
        <td>${esc(k.role)}</td>
        <td>${esc(k.name)}${k.org ? ` <span class="org">· ${esc(k.org)}</span>` : ''}</td>
        <td class="tel">${esc(k.phone)}</td>
        <td class="note">${esc(k.note ?? '')}</td>
      </tr>`,
    )
    .join('');
  return `<!DOCTYPE html><html lang="cs"><head><meta charset="UTF-8"><style>
    * { box-sizing: border-box; }
    body { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #12325a; margin: 0; padding: 10mm; }
    h1 { font-size: 18px; margin: 0 0 2px; }
    .sub { color: #667085; font-size: 11px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
    th { text-align: left; background: #12325a; color: #fff; padding: 6px 8px; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; }
    td { padding: 5px 8px; border-bottom: 1px solid var(--sd-w-e3e0d4, #e3e0d4); vertical-align: top; }
    .cat { font-weight: 700; white-space: nowrap; }
    .tel { font-family: 'JetBrains Mono', ui-monospace, monospace; white-space: nowrap; font-weight: 600; }
    .org, .note { color: #889; }
    tr:nth-child(even) td { background: var(--sd-w-faf8f1, #faf8f1); }
  </style></head><body>
    <h1>Důležitá telefonní čísla</h1>
    <div class="sub">${stavbaNazev ? `Stavba: ${esc(stavbaNazev)} · ` : ''}vlastní čísla · IZS · pohotovosti sítí · energetika — ${rows.length} čísel</div>
    <table>
      <thead><tr><th>Kategorie</th><th>Funkce</th><th>Jméno / organizace</th><th>Telefon</th><th>Poznámka</th></tr></thead>
      <tbody>${body}</tbody>
    </table>
  </body></html>`;
}

const EMPTY: TelefonniCisloData = { funkce: '', jmeno: '', telefon: '', popis: '', stavba_id: null };

function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.detail && typeof err.detail === 'object' && 'detail' in err.detail) {
    return String((err.detail as { detail: unknown }).detail);
  }
  return fallback;
}

export function TelefonyPage() {
  const toast = useToast();
  const { activeStavba, activeStavbaId } = useActiveStavba();
  const cislaQuery = useTelefonniCisla(activeStavbaId);
  const saveCislo = useSaveTelefonniCislo();
  const deleteCislo = useDeleteTelefonniCislo();

  // Editor: null = zavřeno, id null = nové číslo.
  const [edit, setEdit] = useState<{ id: number | null; data: TelefonniCisloData } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const openNew = () => {
    setFormError(null);
    setEdit({ id: null, data: { ...EMPTY, stavba_id: activeStavbaId } });
  };
  const openEdit = (c: TelefonniCislo) => {
    setFormError(null);
    const { id, ...data } = c;
    setEdit({ id, data: { ...EMPTY, ...data, jmeno: data.jmeno ?? '', popis: data.popis ?? '' } });
  };
  const setField = (k: keyof TelefonniCisloData, v: string | number | null) =>
    setEdit((e) => (e ? { ...e, data: { ...e.data, [k]: v } } : e));

  const onSave = async () => {
    if (!edit) return;
    const d = edit.data;
    if (!d.telefon.trim()) return setFormError('Vyplňte telefonní číslo.');
    if (!d.funkce.trim() && !d.jmeno.trim()) return setFormError('Vyplňte funkci nebo jméno.');
    try {
      await saveCislo.mutateAsync({ id: edit.id, data: d });
      toast.success(edit.id ? 'Číslo upraveno.' : 'Číslo přidáno.');
      setEdit(null);
    } catch (err) {
      setFormError(apiErrorText(err, 'Číslo se nepodařilo uložit.'));
    }
  };

  const onDelete = async () => {
    if (!edit?.id) return;
    const nazev = edit.data.funkce || edit.data.jmeno || edit.data.telefon;
    if (!window.confirm(`Opravdu smazat číslo „${nazev}"?`)) return;
    try {
      await deleteCislo.mutateAsync(edit.id);
      toast.success('Číslo smazáno.');
      setEdit(null);
    } catch (err) {
      setFormError(apiErrorText(err, 'Číslo se nepodařilo smazat.'));
    }
  };

  const customRows: Kontakt[] = (cislaQuery.data ?? []).map((c) => ({
    id: `c-${c.id}`,
    typ: 'vlastni',
    role: c.funkce,
    name: c.jmeno ?? '',
    phone: c.telefon,
    note: c.popis || undefined,
    custom: c,
  }));

  const rows = [
    ...customRows,
    ...izsContacts,
    ...utilityContacts,
    ...energyContacts,
  ];

  const columns: ListColumn<Kontakt>[] = [
    {
      key: 'typ',
      label: 'Kategorie',
      width: '140px',
      render: (k) => <Badge tone={typMap[k.typ].tone} pill>{typMap[k.typ].label}</Badge>,
    },
    { key: 'role', label: 'Funkce', primary: true, width: '280px' },
    { key: 'name', label: 'Jméno / organizace' },
    {
      key: 'phone',
      label: 'Telefon',
      width: '160px',
      mono: true,
      render: (k) => {
        const telHref = `tel:${k.phone.replace(/\s/g, '')}`;
        return (
          <a href={telHref} style={{ color: 'inherit', textDecoration: 'underline' }}>
            {k.phone}
          </a>
        );
      },
    },
    {
      key: 'qr',
      label: 'QR',
      width: '72px',
      render: (k) => (
        <DocContactQR
          name={k.name}
          role={k.role}
          org={k.org}
          phone={k.phone}
          compact
          label="QR"
          title={`QR vizitka — ${k.name}`}
        />
      ),
    },
    { key: 'note', label: 'Popis', width: '220px', dim: true, render: (k) => k.note ?? '—' },
    {
      key: 'edit',
      label: '',
      width: '44px',
      render: (k) =>
        k.custom ? (
          <span title="Upravit / smazat" aria-label="Upravit" style={{ cursor: 'pointer' }}>✎</span>
        ) : null,
    },
  ];

  return (
    <ListPage
      breadcrumb={['BOZP', 'Telefonní čísla']}
      title="Důležitá telefonní čísla"
      subtitle={`${customRows.length} vlastních · IZS + pohotovosti sítí + energetika${activeStavba ? ` · stavba ${activeStavba.nazev}` : ''}`}
      status="done"
      statusText={`${rows.length} čísel`}
      actions={
        <>
          <Button variant="primary" leading="+" onClick={openNew}>Přidat číslo</Button>
          <Button variant="secondary" leading="⎙" onClick={() => printToPdf('Telefonni_cisla.pdf', buildPhoneListHtml(rows, activeStavba?.nazev))}>PDF (A4)</Button>
        </>
      }
      searchPlaceholder="Hledat kontakt…"
    >
      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(k) => k.id}
        onRowClick={(k) => k.custom && openEdit(k.custom)}
        emptyTitle="Žádné kontakty"
        emptyText="Seznam pohotovostních a tísňových čísel."
      />
      {cislaQuery.isError && (
        <div role="alert" style={{ color: 'var(--sd-danger, #f87171)', marginTop: 12 }}>
          Vlastní čísla se nepodařilo načíst — zobrazují se jen pevná čísla.
        </div>
      )}

      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.id ? 'Upravit telefonní číslo' : 'Nové telefonní číslo'}
        footer={
          <>
            {edit?.id && (
              <Button variant="danger" onClick={onDelete} disabled={deleteCislo.isPending} style={{ marginRight: 'auto' }}>
                Smazat
              </Button>
            )}
            <Button variant="ghost" onClick={() => setEdit(null)}>Zrušit</Button>
            <Button variant="primary" onClick={onSave} disabled={saveCislo.isPending}>
              {saveCislo.isPending ? 'Ukládám…' : 'Uložit'}
            </Button>
          </>
        }
      >
        {edit && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSave();
            }}
            style={{ display: 'grid', gap: 12 }}
          >
            <div className={styles.grid2}>
              <Input
                label="Funkce"
                placeholder="např. Stavbyvedoucí, Koordinátor BOZP"
                value={edit.data.funkce}
                onChange={(e) => setField('funkce', e.target.value)}
                autoFocus
              />
              <Input
                label="Jméno / firma"
                value={edit.data.jmeno}
                onChange={(e) => setField('jmeno', e.target.value)}
              />
            </div>
            <Input
              label="Telefon"
              required
              mono
              type="tel"
              inputMode="tel"
              placeholder="+420 …"
              value={edit.data.telefon}
              onChange={(e) => setField('telefon', e.target.value)}
            />
            <Input
              label="Popis"
              placeholder="např. dosažitelný 7–15 h, náhradník za …"
              value={edit.data.popis}
              onChange={(e) => setField('popis', e.target.value)}
            />
            {activeStavba ? (
              <Checkbox
                label={`Jen pro stavbu ${activeStavba.nazev}`}
                checked={edit.data.stavba_id != null}
                onChange={(e) => setField('stavba_id', e.target.checked ? activeStavba.id : null)}
              />
            ) : edit.data.stavba_id != null ? (
              <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>Číslo patří ke konkrétní stavbě.</div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--sd-text-dim)' }}>
                Bez vybrané stavby se číslo zobrazí u všech staveb.
              </div>
            )}
            {formError && (
              <div role="alert" style={{ color: 'var(--sd-danger, #f87171)', fontSize: 13 }}>{formError}</div>
            )}
            <button type="submit" hidden />
          </form>
        )}
      </Modal>
    </ListPage>
  );
}
